import { useQuery } from "@tanstack/react-query";
import { apiCall } from "../lib/apiClient";
import {
  AdminDocumentsResponse,
  AdminSyncRunDetail,
  buildDocumentsQueryString,
  type AdminDocumentsFilters,
} from "../schemas/adminDocuments";

/**
 * Admin queries pro UC-001 visibility (princip #4 sync transparency).
 *
 * useAdminDocuments(filters?) → list všech dokumentů (přes všechny tenanty,
 * globalAdmin only). Filtry: zoneId, docType, periodFrom/To.
 *
 * useAdminSyncRunDetail(id?) → per-run aggregát s document/transaction counts
 * + inline mini-list dokumentů (max 50).
 */

const KEY_DOC_LIST = (filters: AdminDocumentsFilters) =>
  ["admin", "documents", filters] as const;
const KEY_SYNC_DETAIL = (id: string) =>
  ["admin", "sync", "runs", id] as const;

export function useAdminDocuments(filters: AdminDocumentsFilters = {}) {
  return useQuery({
    queryKey: KEY_DOC_LIST(filters),
    queryFn: async () => {
      const raw = await apiCall<unknown>(
        `/api/admin/documents${buildDocumentsQueryString(filters)}`,
      );
      return AdminDocumentsResponse.parse(raw);
    },
  });
}

export function useAdminSyncRunDetail(runId: string | null | undefined) {
  return useQuery({
    queryKey: KEY_SYNC_DETAIL(runId ?? "—"),
    queryFn: async () => {
      const raw = await apiCall<unknown>(
        `/api/admin/sync/runs/${runId}`,
      );
      return AdminSyncRunDetail.parse(raw);
    },
    enabled: typeof runId === "string" && runId.length > 0,
  });
}
